import { useCallback, useEffect, useState } from "react";
import { useSendUserOperation, useWaitForUserOperation } from "@coinbase/cdp-hooks";
import type { EvmAddress, EvmUserOperationNetwork, Hex } from "@coinbase/cdp-core";
import type { ContractCall } from "@hybrid-escrow/shared";
import { attributionOptions } from "./attribution";
import { sponsorshipOptions } from "./sponsorship";

export type CheckoutOpPhase =
  | "idle"
  | "awaiting-signature" // wallet prompt/signature in flight
  | "submitted" // user operation hash known, waiting for onchain confirmation
  | "confirmed" // status "complete", transactionHash available
  | "failed";

interface SubmittedOp {
  hash: Hex;
  smartAccount: EvmAddress;
  network: EvmUserOperationNetwork;
}

export interface SubmitParams {
  smartAccountAddress: EvmAddress;
  network: EvmUserOperationNetwork;
  /** Raw `{to, data, value}` calls from the checkout intent response, in call order. */
  calls: ContractCall[];
}

/**
 * Sends the batched USDC `approve` + `fundEscrow` calls as a single Smart Account user
 * operation (per Build.md: batch when the account supports it) and polls until the
 * operation lands onchain or definitively fails. Never approves an unlimited allowance —
 * the exact `approve` call data comes verbatim from the API's checkout-intent response,
 * which encodes the exact `totalDueBaseUnits`.
 *
 * Gas sponsorship (`useCdpPaymaster`) is opt-in per deployment via `VITE_USE_CDP_PAYMASTER`:
 * Build.md requires conservative, explicitly allowlisted Paymaster usage configured in the CDP
 * Portal first. See lib/coinbase/sponsorship.ts and COINBASE_INTEGRATION_NOTES.md.
 */
export function useCheckoutUserOperation() {
  const { sendUserOperation } = useSendUserOperation();
  const [submittedOp, setSubmittedOp] = useState<SubmittedOp | null>(null);
  const [phase, setPhase] = useState<CheckoutOpPhase>("idle");
  const [error, setError] = useState<string | null>(null);

  const wait = useWaitForUserOperation({
    userOperationHash: submittedOp?.hash,
    evmSmartAccount: submittedOp?.smartAccount,
    network: submittedOp?.network,
    enabled: submittedOp !== null,
  });

  useEffect(() => {
    if (!submittedOp) return;
    if (wait.status === "success" && wait.data) {
      if (wait.data.status === "complete") {
        setPhase("confirmed");
      } else if (wait.data.status === "dropped" || wait.data.status === "failed") {
        setPhase("failed");
        setError(
          wait.data.status === "dropped"
            ? "The transaction was dropped by the network. No funds were moved — you can retry."
            : "The transaction failed onchain (reverted). No funds were moved — you can retry.",
        );
      }
      // "pending" / "signed" / "broadcast" -> stay in "submitted", the hook keeps polling.
    } else if (wait.status === "error") {
      setPhase("failed");
      setError(wait.error?.message ?? "Could not confirm the transaction status.");
    }
  }, [wait.status, wait.data, submittedOp]);

  const submit = useCallback(
    async ({ smartAccountAddress, network, calls }: SubmitParams) => {
      setPhase("awaiting-signature");
      setError(null);
      try {
        const result = await sendUserOperation({
          evmSmartAccount: smartAccountAddress,
          network,
          calls: calls.map((call) => ({
            to: call.to as EvmAddress,
            data: call.data as Hex,
            value: BigInt(call.value),
          })),
          ...attributionOptions(),
          ...sponsorshipOptions(),
        });
        setSubmittedOp({
          hash: result.userOperationHash,
          smartAccount: smartAccountAddress,
          network,
        });
        setPhase("submitted");
        return result.userOperationHash;
      } catch (err) {
        setPhase("failed");
        setError(
          err instanceof Error
            ? err.message
            : "The wallet rejected or failed to submit the transaction.",
        );
        return null;
      }
    },
    [sendUserOperation],
  );

  const reset = useCallback(() => {
    setSubmittedOp(null);
    setPhase("idle");
    setError(null);
  }, []);

  return {
    phase,
    userOperationHash: submittedOp?.hash ?? null,
    transactionHash: wait.data?.transactionHash ?? null,
    error,
    submit,
    reset,
  };
}
